// İSG uzmanı portalı — uygulama başlığı (S34-3 Carbon UI Shell, design review
// `docs/reviews/S34-carbon-phase2-plan.md` §3, M7/M8). Sol: marka (SVG `Logo`, §2.3 — PNG
// DEĞİL; "isg" `text-shell-fg`, "port"+baret her zaman turuncu `fill-brand`, TEK marka-turuncu
// kullanımı — M8 "leave the orange header accent out", logo dışında ikinci bir turuncu işaret
// EKLENMEZ) + firma adı (aktif firma `/me`'den; api-contract §18.1) — düz metin, tıklanabilir
// bir tenant menüsü DEĞİL (web-expert'te firma değiştirme yok). Sağ: genel arama (S34-5,
// `HeaderSearch.tsx`) + TEK hesap menüsü (24px baş harf rozeti) — dil seçici artık STANDALONE
// bir kontrol DEĞİL, bu menünün İÇİNDE bir `RadioGroup` (M7). Bildirim rozeti / saha seçici
// KASITLI OLARAK YOK (plan §4, S34-3 kapsamı dışı — saha bağlamı sayfa düzeyinde, review M4).
//
// **S34-5 — genel arama.** `HeaderSearch` yalnız `permits/list` erişimi VARSA render edilir
// (`usePermitsAccess` — SideNav'ın İş İzni menüsüyle AYNI sonda; uç zaten bu kapıya tabi,
// kontrol yalnız 403'e giden gereksiz bir isteği ÖNLER, dosya başlığındaki "asıl yetki
// backend'de" deseninin AYNISI).
//
// **M7 — Dil `LanguageSwitcher` (Radix `Select`) OLARAK GÖMÜLMEZ.** Bir `Select` bir menünün
// İÇİNE yuvalanırsa menünün ok-tuşu roving'i VE Esc sırası KIRILIR (review M7 "a Select nested
// inside a menu breaks the menu's arrow-key roving"). Bunun yerine `changeCulture`/
// `getAvailableLanguages` DOĞRUDAN, `DropdownMenu.RadioGroup` + `RadioItem` ile kullanılır —
// `LanguageSwitcher`in `busy` koruması (aktif değişim sırasında devre dışı) AYNEN mirror edilir.
// Menü seçimde HEMEN KAPANMAZ: her `RadioItem`in `onSelect`i `preventDefault` eder (Radix'in
// "seç + kapat" varsayılanını iptal eder), `changeCulture` çözülünce (`finally`) biz KENDİMİZ
// kapatırız (`menuOpen` kontrollü state) — review "Keep the menu open until it resolves, then
// close it".
//
// Ad-soyad token'da yok — self-profil ucundan gelir; yüklenene dek e-postaya düşer (defensif).
// Rol/firma satırı `sr-only` önekli iki parçadan kurulur ("Rol: X Firma: Y" ekran okuyucuda,
// görsel olarak yalnız "X · Y") — `·` görsel bir ayraç, iki alanı BİRBİRİNDEN ayırt eden gerçek
// bilgi ekran okuyucuya `sr-only` metinle taşınır (aksi hâlde "İSG Uzmanı · Acme A.Ş." hangisi
// rol hangisi firma, sese dönünce belirsizleşir).

import {
  Logo,
  changeCulture,
  clearSiteContext,
  getAvailableLanguages,
  logout,
  useAuthStore,
} from '@isgportal/shared';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, CircleUser, LogOut, UserRound } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { usePermitsAccess } from '../lib/permitsAccess';
import { queryClient } from '../lib/queryClient';
import { useMe } from '../lib/useMe';
import { HeaderSearch } from './HeaderSearch';

/** Baş harf rozeti — ad + soyadın ilk harfleri; ikisi de yoksa `null` (defensif ikon düşer). */
function initialsOf(firstName?: string | null, lastName?: string | null): string | null {
  const a = firstName?.trim()?.[0];
  const b = lastName?.trim()?.[0];
  if (!a && !b) return null;
  return `${a ?? ''}${b ?? ''}`.toUpperCase();
}

export function AppHeader() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const principal = useAuthStore((s) => s.principal);
  const { me } = useMe();
  const languages = getAvailableLanguages();
  // Genel arama yalnız `permits/list` erişimi VARSA görünür (dosya başlığı) — hiçbir grant'ı
  // olmayan principal için uç zaten 403 verirdi, bu yalnız o isteği baştan ÖNLER.
  const permits = usePermitsAccess();

  const [menuOpen, setMenuOpen] = useState(false);
  const [languageBusy, setLanguageBusy] = useState(false);

  // Header etiketi: yalnız firma adı. Firma adı yoksa (yüklenmedi / çözülemedi) portal
  // etiketine düşülür (defensif — §18: firma alanları güvenle boş kalabilir; boş header
  // yerine bir şey gösterilir).
  const portalLabel = t('app.expertPortal');
  const companyName = me?.companyName?.trim();
  const headerLabel = companyName || portalLabel;

  // Görünen kimlik: ad-soyad (self-profil) — yoksa e-posta (token) — yoksa "Hesap".
  const fullName = me ? `${me.firstName} ${me.lastName}`.trim() : '';
  const displayName = fullName || principal?.email || t('layout.account');
  const initials = initialsOf(me?.firstName, me?.lastName);
  const roleName = me?.roleName?.trim();

  async function onLanguageChange(culture: string) {
    if (culture === i18n.language) {
      setMenuOpen(false);
      return;
    }
    setLanguageBusy(true);
    try {
      await changeCulture(culture);
    } finally {
      setLanguageBusy(false);
      setMenuOpen(false);
    }
  }

  function onLogout() {
    logout();
    // `/security` önbellekte site listesi + gün tablosu (ad, karar, ret sebebi) taşır —
    // `logout()` yalnız auth store'u temizler, TanStack önbelleğini temizlemez; çıkışta
    // silinmezse aynı gate PC'sinde giriş yapan sonraki kullanıcı önceki firmanın/kullanıcının
    // verisini AĞ İSTEĞİ OLMADAN bellekte bulur (web-supplier `AppHeader.tsx`'teki S17 güvenlik
    // bulgusuyla AYNI desen — birebir mirror edildi).
    queryClient.clear();
    // `useSiteContext`in `localStorage` anahtarı artık `principal.uuid`e göre ayrılır (bkz.
    // `siteContext.ts` dosya başlığı, güvenlik düzeltmesi S34-3 sonrası) — bu YETERLİ DEĞİL, bir
    // sonraki kullanıcı hâlâ AYNI sekmede/pencerede oturum açarsa bellek-içi zustand store'u
    // (`hydrate` yalnız `uuid` DEĞİŞTİĞİNDE koşar, `SecurityDeskPage` bu geçişten önce zaten
    // unmount olmuş olabilir) çıkan kullanıcının SON seçili sahasını taşımaya devam edebilir.
    // `queryClient.clear()` ile AYNI yerde, AYNI gerekçeyle senkron temizlenir.
    clearSiteContext();
    navigate('/login', { replace: true });
  }

  return (
    <header className="flex h-12 items-stretch justify-between border-b border-shell-border bg-shell text-shell-fg">
      <div className="flex min-w-0 items-center gap-2.5 px-3">
        <Logo className="h-9 shrink-0 text-shell-fg" title={t('app.name')} />
        <span className="hidden text-shell-fg-muted sm:inline" aria-hidden="true">
          ·
        </span>
        <span className="hidden max-w-[24rem] truncate text-sm text-shell-fg-muted sm:inline">
          {headerLabel}
        </span>
      </div>

      <div className="flex items-stretch">
        {permits.status === 'granted' ? <HeaderSearch /> : null}
        <DropdownMenu.Root open={menuOpen} onOpenChange={setMenuOpen}>
          <DropdownMenu.Trigger
            className="flex h-12 w-12 items-center justify-center text-shell-fg-muted hover:bg-shell-hover hover:text-shell-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-shell-fg"
            aria-label={t('layout.user.menu')}
          >
            <span className="flex h-6 w-6 items-center justify-center rounded-full bg-shell-avatar text-[11px] font-semibold text-fg">
              {initials ?? <UserRound className="h-3.5 w-3.5" aria-hidden="true" />}
            </span>
          </DropdownMenu.Trigger>
          <DropdownMenu.Portal>
            <DropdownMenu.Content
              align="end"
              sideOffset={6}
              className="z-[var(--z-dropdown)] min-w-[16rem] max-w-[20rem] overflow-hidden rounded-md border border-border bg-surface p-1 shadow-md"
            >
              <div className="px-2 py-1.5">
                <p className="text-xs text-muted">{t('layout.user.signedInAs')}</p>
                <p className="truncate text-sm font-medium text-fg" title={displayName}>
                  {displayName}
                </p>
                {me?.email ? (
                  <p className="truncate text-xs text-muted" title={me.email}>
                    {me.email}
                  </p>
                ) : null}
                {roleName || companyName ? (
                  <p
                    className="truncate text-xs text-muted"
                    title={[roleName, companyName].filter(Boolean).join(' · ')}
                  >
                    {roleName ? (
                      <>
                        <span className="sr-only">{t('layout.user.role')}: </span>
                        {roleName}
                      </>
                    ) : null}
                    {roleName && companyName ? ' · ' : null}
                    {companyName ? (
                      <>
                        <span className="sr-only">{t('layout.user.company')}: </span>
                        {companyName}
                      </>
                    ) : null}
                  </p>
                ) : null}
              </div>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
              <DropdownMenu.Item
                onSelect={() => navigate('/profile')}
                className="flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-sm text-fg outline-none data-[highlighted]:bg-surface-muted"
              >
                <CircleUser className="h-4 w-4 text-muted" aria-hidden="true" />
                {t('layout.profile')}
              </DropdownMenu.Item>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
              <DropdownMenu.Label className="px-2 py-1 text-xs text-muted">
                {t('layout.user.language')}
              </DropdownMenu.Label>
              <DropdownMenu.RadioGroup value={i18n.language} onValueChange={onLanguageChange}>
                {languages.map((lang) => (
                  <DropdownMenu.RadioItem
                    key={lang.culture}
                    value={lang.culture}
                    disabled={languageBusy}
                    onSelect={(event) => event.preventDefault()}
                    // S34-4 QA fix — seçili öğenin ada-göre-hesaplanan (name-from-content)
                    // erişilebilir adı boş ölçülüyordu (yalnız seçili DEĞİLKEN doğru raporlanıyor;
                    // görsel metin her zaman doğru). Kök neden Radix/tarayıcı etkileşiminde
                    // belirsiz — kalıcı düzeltme: adı içerikten TÜRETMEK yerine AÇIKÇA vermek,
                    // kontrol/odak durumundan bağımsız hâle getirir.
                    aria-label={lang.name}
                    className="relative flex cursor-pointer select-none items-center gap-2 rounded py-1.5 pl-8 pr-2 text-sm text-fg outline-none data-[highlighted]:bg-surface-muted data-[disabled]:pointer-events-none data-[disabled]:opacity-60"
                  >
                    <DropdownMenu.ItemIndicator className="absolute left-2 inline-flex">
                      <Check className="h-3.5 w-3.5 text-primary" aria-hidden="true" />
                    </DropdownMenu.ItemIndicator>
                    {lang.name}
                  </DropdownMenu.RadioItem>
                ))}
              </DropdownMenu.RadioGroup>
              <DropdownMenu.Separator className="my-1 h-px bg-border" />
              <DropdownMenu.Item
                onSelect={onLogout}
                className="flex cursor-pointer select-none items-center gap-2 rounded px-2 py-1.5 text-sm text-danger outline-none data-[highlighted]:bg-danger-subtle"
              >
                <LogOut className="h-4 w-4" aria-hidden="true" />
                {t('layout.logout')}
              </DropdownMenu.Item>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </header>
  );
}
